/**
 * DRF (Debt Retirement Fund) calculations for municipal bond processing
 * All functions are pure (Level 5) with no side effects
 * @module calculators/cashflow/drf-calculations
 */

/**
 * Calculates the DRF (Debt Retirement Fund) adjustment for a payment period
 * Positive = money coming into DRF (revenue exceeds payments)
 * Negative = money going out (payments exceed revenue)
 * @param revenueMinusExistingDebt - Available revenue after existing obligations
 * @param totalBondPayment - Total bond payment for the period (principal + interest)
 * @returns DRF adjustment amount (can be positive or negative)
 * @pure Level 5 calculation - no side effects
 */
export function calculateDrfAdjustment(
  revenueMinusExistingDebt: number,
  totalBondPayment: number
): number {
  return revenueMinusExistingDebt - totalBondPayment;
}

/**
 * Calculates the DRF ending balance after applying the period's adjustment
 * @param startingBalance - DRF balance at start of period
 * @param adjustment - DRF adjustment for the period (positive or negative)
 * @returns DRF ending balance for the period
 * @pure Level 5 calculation - no side effects
 */
export function calculateDrfEndingBalance(startingBalance: number, adjustment: number): number {
  return startingBalance + adjustment;
}

/**
 * Calculates the DRF net change for a period (same as adjustment)
 * This function exists for semantic clarity when specifically referring to the change amount
 * @param adjustment - DRF adjustment for the period
 * @returns Net change in DRF balance
 * @pure Level 5 calculation - no side effects
 */
export function calculateDrfNetChange(adjustment: number): number {
  return adjustment;
}

/**
 * Calculates the DRF net revenue by combining payment revenue portion with DRF adjustment
 * @param paymentRevenuePortion - Revenue portion allocated to bond payments
 * @param adjustment - DRF adjustment for the period
 * @returns Total net revenue including DRF impact
 * @pure Level 5 calculation - no side effects
 */
export function calculateDrfNetRevenue(paymentRevenuePortion: number, adjustment: number): number {
  return paymentRevenuePortion + adjustment;
}
